/* eslint-disable @typescript-eslint/no-explicit-any */
// components/ViewResumeModal.tsx
import React from "react";
import { useDarkMode } from "../context/DarkModeContext";

interface ViewResumeModalProps {
  resumeUrl: string;
  isOpen: boolean;
  onClose: () => void;
  applicantName?: string;
}

const ViewResumeModal: React.FC<ViewResumeModalProps> = ({
  resumeUrl,
  isOpen,
  onClose,
  applicantName = "Applicant",
}) => {
  const { isDark } = useDarkMode();

  if (!isOpen) return null;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = `resume-${applicantName
      .replace(/\s+/g, "-")
      .toLowerCase()}.pdf`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExternalOpen = () => {
    window.open(resumeUrl, "_blank", "noopener,noreferrer");
  };

  // Check if the URL is accessible and is a PDF
  const isPdfUrl =
    resumeUrl.toLowerCase().includes(".pdf") ||
    resumeUrl.toLowerCase().includes("application/pdf");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg w-full max-w-4xl h-full max-h-[90vh] flex flex-col ${
          isDark ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`flex justify-between items-center p-4 border-b ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h3
              className={`text-lg font-semibold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Resume Preview - {applicantName}
            </h3>
            <p
              className={`text-sm mt-1 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {isPdfUrl ? "PDF Document" : "Resume File"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Download
            </button>
            <button
              onClick={handleExternalOpen}
              className={`px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                isDark
                  ? "bg-gray-600 text-white hover:bg-gray-500"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
            >
              Open in New Tab
            </button>
            <button
              onClick={onClose}
              className={`p-2 transition-colors rounded-full ${
                isDark
                  ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className={`flex-1 p-4 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <div
            className={`rounded-lg shadow-sm h-full overflow-hidden ${
              isDark ? "bg-gray-800" : "bg-white"
            }`}
          >
            {resumeUrl && isPdfUrl ? (
              <iframe
                src={resumeUrl}
                className="w-full h-full border-0"
                title={`Resume of ${applicantName}`}
                onError={(e) => {
                  console.error("Failed to load PDF in iframe:", e);
                  // Fallback to download if iframe fails
                  handleDownload();
                }}
              />
            ) : resumeUrl ? (
              <div className="flex flex-col items-center justify-center h-full p-8">
                <svg
                  className={`w-16 h-16 mb-4 ${
                    isDark ? "text-gray-500" : "text-gray-400"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p
                  className={`mb-4 text-center ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  This resume format cannot be previewed directly.
                </p>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Download Resume
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <svg
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-500" : "text-gray-400"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No resume available
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`p-4 border-t ${
            isDark
              ? "bg-gray-800 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div
            className={`flex justify-between items-center text-sm ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            <span>
              {isPdfUrl ? "PDF Document" : "File"} • {applicantName}
            </span>
            <div className="flex gap-4">
              {isPdfUrl && (
                <>
                  <button
                    onClick={() => {
                      const iframe = document.querySelector("iframe");
                      if (iframe) {
                        (iframe.contentWindow as any)?.print?.();
                      }
                    }}
                    className={`transition-colors ${
                      isDark
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    Print
                  </button>
                  <span className={isDark ? "text-gray-600" : "text-gray-400"}>
                    •
                  </span>
                </>
              )}
              <a
                href={resumeUrl}
                download
                className={`transition-colors ${
                  isDark
                    ? "text-blue-400 hover:text-blue-300"
                    : "text-blue-600 hover:text-blue-800"
                }`}
              >
                Direct Download
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewResumeModal;
